import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { selectSessionAdvisor, selectAdvisorAdminPortalState } from '@/model/selectors';
import {
  resetAdvisorAdminPortal,
  postAdvisorForAdminSearchRequest,
} from '@/model/advisorAdminPortalModel';
import {
  clearSubscriptionSeats,
  fetchSubscriptionSeatsByAdvisorIdRequest,
} from '@/model/subscriptionSeatsModel';
import {
  clearAdvisorHierarchyRelationships,
  fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest,
} from '@/model/advisorHierarchyModel';
import { isWizard } from '@shared/isWizard';

interface UseAdminModeOptions {
  propAdminMode?: boolean;
  propShowAdvisorSearch?: boolean;
}

export function useAdminMode({
  propAdminMode = false,
  propShowAdvisorSearch = false,
}: UseAdminModeOptions = {}) {
  const dispatch = useDispatch();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { selectedAdvisor } = useSelector(selectAdvisorAdminPortalState);
  const [searchParams, setSearchParams] = useSearchParams();

  // Read state from query params (source of truth)
  const adminModeParam = searchParams.get('adminMode') === 'true';
  const advisorIdParam = searchParams.get('advisorId');

  // Derive admin mode state from URL - this is the source of truth
  const isAdminModeEnabled = propAdminMode || adminModeParam;
  const selectedAdvisorId = advisorIdParam || null;

  const [hasInitializedFromParams, setHasInitializedFromParams] = useState(false);

  // Check if current user is a wizard
  const isWizardUser = sessionAdvisor && isWizard(sessionAdvisor);

  // Admin mode is active if explicitly enabled via props OR user toggled it on
  const adminModeActive = propAdminMode || isAdminModeEnabled;
  const showSearch = propShowAdvisorSearch || isAdminModeEnabled;

  // Fetch advisor details on mount if advisorId is in query params
  useEffect(() => {
    if (!hasInitializedFromParams && advisorIdParam && !selectedAdvisor) {
      dispatch(postAdvisorForAdminSearchRequest({ advisorId: advisorIdParam }));
      setHasInitializedFromParams(true);
    }
  }, [advisorIdParam, selectedAdvisor, hasInitializedFromParams, dispatch]);

  const handleAdvisorSelected = useCallback(
    (advisor: Advisor) => {
      // Update URL first
      const newParams = new URLSearchParams(searchParams);
      newParams.set('advisorId', advisor.advisorId);
      setSearchParams(newParams, { replace: true });

      dispatch(fetchSubscriptionSeatsByAdvisorIdRequest(advisor.advisorId));
      dispatch(
        fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest({
          advisorId: advisor.advisorId,
        }),
      );
    },
    [dispatch, searchParams, setSearchParams],
  );

  const handleToggleChange = useCallback(
    (checked: boolean) => {
      const newParams = new URLSearchParams(searchParams);

      if (checked) {
        newParams.set('adminMode', 'true');
      } else {
        // Reset when leaving admin mode
        newParams.delete('adminMode');
        newParams.delete('advisorId');
        dispatch(resetAdvisorAdminPortal());
        dispatch(clearSubscriptionSeats());
        dispatch(clearAdvisorHierarchyRelationships());

        if (sessionAdvisor?.advisorId) {
          dispatch(fetchSubscriptionSeatsByAdvisorIdRequest(sessionAdvisor.advisorId));
          dispatch(
            fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest({
              advisorId: sessionAdvisor.advisorId,
            }),
          );
        }
      }

      setSearchParams(newParams, { replace: true });
    },
    [dispatch, searchParams, setSearchParams, sessionAdvisor?.advisorId],
  );

  const handleChangeAdvisor = useCallback(() => {
    // Clear selection and return to search
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('advisorId');
    setSearchParams(newParams, { replace: true });

    dispatch(resetAdvisorAdminPortal());
    dispatch(clearSubscriptionSeats());
    // Note: We intentionally don't clear hierarchy relationships here.
    // Clearing would set status to 'idle', which triggers the hook to fetch
    // for the session advisor (since no advisor is selected).
    // The stale data is hidden because showTeamUI is false when no advisor is selected.
  }, [dispatch, searchParams, setSearchParams]);

  const hasSelectedAdvisor = Boolean(selectedAdvisorId);

  return {
    // State
    isWizardUser,
    isAdminModeEnabled,
    selectedAdvisorId,
    adminModeActive,
    showSearch,
    hasSelectedAdvisor,

    // Handlers
    handleAdvisorSelected,
    handleToggleChange,
    handleChangeAdvisor,
  };
}
